
import { NoData } from "@/components/common/section-components/NoData";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { FuelSlotRequestActions } from "@/components/common/section-components/action/FuelSlotRequestActions";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { FuelSlotRequestFilter } from "@/components/common/section-components/filters/FuelSlotRequestFilter";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { SuppliesRequestStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { usePolling } from "@/hooks/usePolling";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  clearFiltersHandler,
  fetchFuelSlotRequestsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import {
  FuelSlotRequestEntity,
  FuelSlotRequestListResponse,
} from "./schema/fuel-slot-requests.schema";
import { fuelSlotRequestsHeaderElements } from "./schema/header-elements";

export const FuelSlotRequestsPage = () => {
  const MAX_PRODUCT_TYPES_DISPLAY = 3;

  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const { control, handleSubmit, setValue } = useForm();
  const values = useWatch({ control });
  const filtersEmpty = useMemo(
    () => Object.values(values ?? {}).every((v) => isEmpty(v)),
    [values],
  );

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<FuelSlotRequestListResponse>();
  const [currentFilters, setCurrentFilters] = useState<any>();
  const [page, setPage] = useState<number>(1);

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchFuelSlotRequestsHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchList();
    }
  }, [isActive]);

  const submitFilters = async (event: FieldValues) => {
    await submitFiltersHandler({
      event,
      setPage,
      fetchList,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      filtersEmpty,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      setCurrentFilters,
      fetchList,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (item: FuelSlotRequestEntity) => {
    navigate(AppRoutes.FUEL_SLOTS_REQ_DETAIL.replace(":id", String(item.id)));
  };

  const getActionElements = (item: FuelSlotRequestEntity) => (
    <FuelSlotRequestActions handleDetail={handleDetail} item={item} />
  );

  usePolling({
    callback: () => {
      if (!loading) fetchList({ ...currentFilters }, false);
    },
    delay: 12500,
    isActive,
  });

  const getProductTypeDisplay = (el: FuelSlotRequestEntity) => {
     const types = el?.tipoProducto ?? [];
    if (!types.length) return "-";
    const displayed = types
        .slice(0, MAX_PRODUCT_TYPES_DISPLAY)
        .join(", ");
    return types.length > MAX_PRODUCT_TYPES_DISPLAY
        ? `${displayed}...`
        : displayed;
  };

  const getProductTypeTitle = (el: FuelSlotRequestEntity) => {
    const types = el?.tipoProducto ?? [];
    return types?.length ? types.join(", ") : "";
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader title="Solicitud de cupos de combustible" loading={loading} />

      <FuelSlotRequestFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        filtersApplied={filtersApplied}
        disableFilters={loading}
        loadingFilters={loading}
        disableApply={filtersEmpty && !filtersApplied}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !list?.listado?.length) && <NoData />}

        {Boolean(!loading && list?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                list?.listado?.map((el: FuelSlotRequestEntity) => ({
                  ...el,
                  fechaEntrega: el?.fechaEntrega
                    ? dayjs(el?.fechaEntrega).format("DD/MM/YYYY")
                    : "-",
                  cantidadLitros: `${new Intl.NumberFormat("es-AR").format(
                    el?.cantidad ?? 0,
                  )}`,
                  tipo: (
                    <span title={getProductTypeTitle(el)}>
                      {getProductTypeDisplay(el)}
                    </span>
                  ),
                  displayEstado: (
                    <RequestStatusBadge
                      status={
                        el?.estadoDescripcion as unknown as SuppliesRequestStatus
                      }
                    />
                  ),
                })) ?? []
              }
              headers={fuelSlotRequestsHeaderElements}
              actionElements={getActionElements}
            />
            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
